import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { useContext, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

import type {
	useCanAddBillingForTrialProductUserCanProductUserAddBillingQuery as useCanAddBillingForTrialProductUserCanProductUserAddBillingQuery$data,
	useCanAddBillingForTrialProductUserCanProductUserAddBillingQueryVariables as useCanAddBillingForTrialProductUserCanProductUserAddBillingQuery$variables,
} from './__types__/useCanAddBillingForTrialProductUserCanProductUserAddBillingQuery.ts';
import type { useCanAddBillingForTrialProductUserTrialDetailsQuery as useCanAddBillingForTrialProductUserTrialDetailsQuery$data } from './__types__/useCanAddBillingForTrialProductUserTrialDetailsQuery.ts';
import { MPU_QUERY } from './mpuHookConstants.ts';

export const useCanAddBillingForTrialProductUser = (skip: boolean = false) => {
	const { isSiteAdmin, isAnonymous } = useContext(SPAViewContext);
	const { cloudId, edition, isLoggedIn, accessStatus } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const meetsBaseEligibility =
		!isSiteAdmin &&
		!isAnonymous &&
		isLoggedIn &&
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		(edition === ConfluenceEdition.STANDARD || edition === ConfluenceEdition.PREMIUM) &&
		!skip;

	// 1. determine billing system (both control & variation need to make the Commerce request)
	const {
		data: { license } = { license: null },
		error: trialDetailsError,
		loading: trialDetailsLoading,
	} = useQuery<useCanAddBillingForTrialProductUserTrialDetailsQuery$data>(
		/** @schema cc */ gql`
			query useCanAddBillingForTrialProductUserTrialDetailsQuery {
				license {
					billingSourceSystem
					licenseConsumingUserCount
					trialEndDate
				}
			}
		`,
		{
			skip: !meetsBaseEligibility,
		},
	);

	// 2. if CCP, make commerce query and determine if they meet backend eligibility
	const {
		data: commerceData,
		error: commerceError,
		loading: commerceLoading,
	} = useQuery<
		useCanAddBillingForTrialProductUserCanProductUserAddBillingQuery$data,
		useCanAddBillingForTrialProductUserCanProductUserAddBillingQuery$variables
	>(
		/** @schema agg */ gql`
			query useCanAddBillingForTrialProductUserCanProductUserAddBillingQuery(
				$cloudId: ID!
				$productKey: String!
			) {
				tenantContexts(cloudIds: [$cloudId])
					@optIn(to: ["CcpAllUserUpgradeAndPay", "HamsAllUserUpgradeAndPay"]) {
					entitlementInfo(hamsProductKey: $productKey) {
						entitlement(where: { AND: [{ inTrialOrPreDunning: true }] }) {
							transactionAccount {
								isCurrentUserBillingAdmin
							}
							subscription {
								accountDetails {
									invoiceGroup {
										invoiceable
										experienceCapabilities {
											configurePaymentV2 {
												experienceUrl
												isAvailableToUser
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			client: getAGGClient(),
			variables: {
				cloudId,
				productKey: MPU_QUERY.HAMS_PRODUCT_KEY,
			},
			skip: !meetsBaseEligibility || license?.billingSourceSystem !== 'CCP',
		},
	);

	const errors = [trialDetailsError, commerceError];

	errors
		.filter((error?: ApolloErrorInterop): error is ApolloErrorInterop => !!error)
		.forEach((error: ApolloErrorInterop) => {
			!isErrorMarkedAsHandled(error) && markErrorAsHandled(error);
		});

	if (commerceError) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'canProductUserPay',
				source: 'useCanAddBillingForTrialProductUser',
				attributes: {
					edition,
					isSiteAdmin,
				},
			},
		}).fire();
	}
	const { isBillingAdmin, canAddBillingDetails, hasBillingDetails } =
		parseCommerceResponse(commerceData);

	const canAddBillingForTrial =
		meetsBaseEligibility &&
		license?.billingSourceSystem === 'CCP' &&
		canAddBillingDetails &&
		!hasBillingDetails;

	const analyticsAttributes = useMemo(
		() => ({
			isSiteAdmin,
			isBillingAdmin: canAddBillingForTrial ? isBillingAdmin : undefined,
		}),
		[isSiteAdmin, canAddBillingForTrial, isBillingAdmin],
	);

	return {
		loading: commerceLoading || trialDetailsLoading,
		canAddBillingForTrial,
		// canAddBillingDetails lets us know that the site is either in trial OR pre-dunning
		//   Then a null trialEndDate means pre-dunning
		isPreDunning: canAddBillingDetails && license?.trialEndDate === null,
		isBillingAdmin,
		hasBillingDetails,
		analyticsAttributes,
		commerceError,
	};
};

const parseCommerceResponse = (data: any) => {
	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;
	const transactionAccount = entitlement?.transactionAccount;
	const invoiceGroup = entitlement?.subscription?.accountDetails?.invoiceGroup;
	const configurePayment = invoiceGroup?.experienceCapabilities?.configurePaymentV2;

	return {
		isBillingAdmin: !!transactionAccount?.isCurrentUserBillingAdmin,
		canAddBillingDetails: !!configurePayment?.isAvailableToUser,
		hasBillingDetails: invoiceGroup?.invoiceable,
	};
};
